<template>
  <v-dialog
    v-model="visible"
    :max-width="isMobile ? undefined : 360"
    :fullscreen="isMobile"
    persistent
  >
    <v-card color="grey darken-4" dark :class="{ 'fill-height d-flex flex-column': isMobile }">
      <v-card-title class="py-2">
        <v-icon left small>mdi-gamepad-variant</v-icon>
        Fernsteuerung
        <v-spacer />
        <v-chip x-small :color="active ? 'green' : 'grey'" dark>
          {{ active ? 'aktiv' : 'aus' }}
        </v-chip>
      </v-card-title>
      <v-divider />
      <v-card-text class="text-center pa-4" :class="{ 'flex-grow-1 overflow-y-auto': isMobile }">
        <div
          ref="pad"
          class="joystick-pad"
          @pointerdown="onDown"
          @pointermove="onMove"
          @pointerup="onUp"
          @pointercancel="onUp"
          @pointerleave="onUp"
        >
          <div class="pad-axis pad-axis-x" />
          <div class="pad-axis pad-axis-y" />
          <div
            class="pad-knob"
            :style="{ transform: `translate(${kx}px, ${ky}px)` }"
          />
        </div>

        <div class="readout mt-3">
          <span class="readout-label">v</span>
          <span class="readout-value">{{ velocity.toFixed(2) }} m/s</span>
          <span class="readout-sep">·</span>
          <span class="readout-label">ω</span>
          <span class="readout-value">{{ omega.toFixed(2) }} rad/s</span>
        </div>

        <div class="hint mt-2">
          <v-icon x-small>mdi-keyboard-outline</v-icon>
          Pfeiltasten oder W A S D · Shift = halbe Geschwindigkeit
        </div>

        <div class="opt-group mt-4">
          <div class="opt-label">Saugkraft</div>
          <v-btn-toggle :value="currentFan" mandatory dense dark background-color="grey darken-3" class="flex-wrap justify-center">
            <v-tooltip v-for="o in fanLevels" :key="'fan-'+o.value" top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  :value="o.value"
                  x-small
                  :disabled="busyFan"
                  v-on="on"
                  @click="applyFan(o.value)"
                >
                  <v-icon x-small left>{{ o.icon }}</v-icon>{{ o.label }}
                </v-btn>
              </template>
              <span>{{ o.hint }}</span>
            </v-tooltip>
          </v-btn-toggle>
        </div>

        <div class="opt-group mt-3">
          <div class="opt-label">Wasser</div>
          <v-btn-toggle :value="currentWater" mandatory dense dark background-color="grey darken-3" class="flex-wrap justify-center">
            <v-tooltip v-for="o in waterModes" :key="'water-'+o.value" top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  :value="o.value"
                  x-small
                  :disabled="busyWater"
                  v-on="on"
                  @click="applyWater(o.value)"
                >
                  <v-icon x-small left>{{ o.icon }}</v-icon>{{ o.label }}
                </v-btn>
              </template>
              <span>{{ o.hint }}</span>
            </v-tooltip>
          </v-btn-toggle>
        </div>

        <div class="opt-group mt-3">
          <div class="opt-label">Wischen</div>
          <v-btn-toggle :value="currentMop" mandatory dense dark background-color="grey darken-3" class="flex-wrap justify-center">
            <v-tooltip v-for="o in mopModes" :key="'mop-'+o.value" top open-delay="200">
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  :value="o.value"
                  x-small
                  :disabled="busyMop"
                  v-on="on"
                  @click="applyMop(o.value)"
                >
                  {{ o.label }}
                </v-btn>
              </template>
              <span>{{ o.hint }}</span>
            </v-tooltip>
          </v-btn-toggle>
        </div>

        <v-alert v-if="error" type="error" class="mt-3 mb-0" dense>{{ error }}</v-alert>
      </v-card-text>
      <v-divider />
      <v-card-actions class="justify-end">
        <v-btn text @click="close">Schließen</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { vacuumApi, RC_MAX_OMEGA, RC_MAX_VELOCITY, FAN_LEVELS, WATER_MODES, MOP_MODES } from '@/services/vacuum'

const PAD_RADIUS = 110            // sichtbarer Joystick-Radius in px
const SEND_INTERVAL_MS = 360      // Keep-alive-Loop — halbiert ggü. früher, um den Sauger nicht zu fluten
const SEND_DURATION_MS = 1700     // duration_ms je Move (deckt HOLD_SKIP_FACTOR×SEND_INTERVAL_MS ab, damit beim Halten keine Lücke entsteht)
const HOLD_SKIP_FACTOR = 4        // Beim Halten nur jeden 4. Tick senden → ~1440 ms zwischen Sends
const TAP_RAMP_MS = 220           // Wie lange omega nach Tastendruck halbiert bleibt

export default {
  name: 'RcDialog',
  props: {
    value: Boolean,
    status: { type: Object, default: null },
  },
  data: () => ({
    active: false,
    error: '',
    kx: 0,
    ky: 0,
    pointerId: null,
    sendTimer: null,
    lastSent: { omega: null, velocity: null },
    lastSendAt: 0, // Drosselt sendImmediate auf SEND_INTERVAL_MS
    holdSkipCount: 0, // Zählt unveränderte Ticks; nur jeder HOLD_SKIP_FACTOR-te wird gesendet
    keys: { up: false, down: false, left: false, right: false, slow: false },
    tapStart: 0, // Zeitstempel des jüngsten Richtungs-Tastendrucks (für Tap-Ramp)
    fanLevels: FAN_LEVELS,
    waterModes: WATER_MODES,
    mopModes: MOP_MODES,
    overrideFan: null,   // optimistischer Wert bis nächstes Status-Update kommt
    overrideWater: null,
    overrideMop: null,
    busyFan: false,
    busyWater: false,
    busyMop: false,
  }),
  computed: {
    visible: {
      get() { return this.value },
      set(v) { this.$emit('input', v) },
    },
    isMobile() {
      return this.$vuetify.breakpoint.xsOnly
    },
    omega() {
      // X-Achse: rechts = robot dreht rechts. API erwartet das vertauscht,
      // daher Vorzeichen invertieren (verifiziert mit dem echten Sauger).
      return (-this.kx / PAD_RADIUS) * RC_MAX_OMEGA
    },
    velocity() {
      // Y-Achse: oben = vorwärts → Y-Wert invertieren.
      return (-this.ky / PAD_RADIUS) * RC_MAX_VELOCITY
    },
    currentFan() {
      if (this.overrideFan !== null) return this.overrideFan
      return this.status && this.status.status && this.status.status.fan_power
    },
    currentWater() {
      if (this.overrideWater !== null) return this.overrideWater
      return this.status && this.status.status && this.status.status.water_box_mode
    },
    currentMop() {
      if (this.overrideMop !== null) return this.overrideMop
      return this.status && this.status.status && this.status.status.mop_mode
    },
  },
  watch: {
    value(open) {
      if (open) this.activate()
      else this.deactivate()
    },
    status() {
      // Sobald frischer Status reinkommt, Overrides verwerfen.
      this.overrideFan = null
      this.overrideWater = null
      this.overrideMop = null
    },
  },
  beforeDestroy() {
    this.deactivate()
  },
  methods: {
    async activate() {
      this.error = ''
      try {
        await vacuumApi.rcStart()
        this.active = true
        this.sendTimer = setInterval(this.tickSend, SEND_INTERVAL_MS)
        window.addEventListener('keydown', this.onKeyDown)
        window.addEventListener('keyup', this.onKeyUp)
        window.addEventListener('blur', this.releaseAllKeys)
      } catch (e) {
        this.error = 'RC-Modus konnte nicht gestartet werden: ' + e.message
      }
    },
    async deactivate() {
      clearInterval(this.sendTimer)
      this.sendTimer = null
      window.removeEventListener('keydown', this.onKeyDown)
      window.removeEventListener('keyup', this.onKeyUp)
      window.removeEventListener('blur', this.releaseAllKeys)
      this.releaseAllKeys()
      this.kx = 0
      this.ky = 0
      this.lastSent = { omega: null, velocity: null }
      if (this.active) {
        this.active = false
        try { await vacuumApi.rcEnd() } catch (e) { /* still */ }
      }
    },
    close() {
      this.visible = false
    },
    onDown(e) {
      if (!this.active) return
      this.pointerId = e.pointerId
      this.$refs.pad.setPointerCapture(e.pointerId)
      this.updateKnob(e)
      this.sendImmediate()
    },
    onMove(e) {
      if (this.pointerId !== e.pointerId) return
      this.updateKnob(e)
      this.sendImmediate()
    },
    async onUp(e) {
      if (this.pointerId !== e.pointerId) return
      this.pointerId = null
      // Knob bleibt am Tastatur-Vektor stehen, falls Pfeiltasten gehalten werden.
      this.updateKnobFromKeys()
      if (this.active && !this.anyDirectionKey()) {
        this.handleStop()
      }
    },
    updateKnob(e) {
      const rect = this.$refs.pad.getBoundingClientRect()
      const cx = rect.left + rect.width / 2
      const cy = rect.top + rect.height / 2
      let dx = e.clientX - cx
      let dy = e.clientY - cy
      const d = Math.hypot(dx, dy)
      if (d > PAD_RADIUS) {
        dx = (dx / d) * PAD_RADIUS
        dy = (dy / d) * PAD_RADIUS
      }
      this.kx = dx
      this.ky = dy
    },
    keyToAction(e) {
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          return 'up'
        case 'ArrowDown':
        case 's':
        case 'S':
          return 'down'
        case 'ArrowLeft':
        case 'a':
        case 'A':
          return 'left'
        case 'ArrowRight':
        case 'd':
        case 'D':
          return 'right'
        case 'Shift':
          return 'slow'
        default:
          return null
      }
    },
    onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.close()
        return
      }
      const action = this.keyToAction(e)
      if (!action) return
      e.preventDefault()
      if (e.repeat) return
      this.keys[action] = true
      if (action !== 'slow') this.tapStart = Date.now()
      this.updateKnobFromKeys()
      this.sendImmediate()
    },
    onKeyUp(e) {
      const action = this.keyToAction(e)
      if (!action) return
      e.preventDefault()
      this.keys[action] = false
      this.updateKnobFromKeys()
      if (!this.anyDirectionKey()) this.handleStop()
      else this.sendImmediate()
    },
    anyDirectionKey() {
      return this.keys.up || this.keys.down || this.keys.left || this.keys.right
    },
    releaseAllKeys() {
      this.keys = { up: false, down: false, left: false, right: false, slow: false }
      this.updateKnobFromKeys()
    },
    updateKnobFromKeys() {
      if (!this.anyDirectionKey() && this.pointerId === null) {
        this.kx = 0
        this.ky = 0
        return
      }
      if (this.pointerId !== null) return // Maus-Drag hat Vorrang
      const vx = (this.keys.right ? 1 : 0) - (this.keys.left ? 1 : 0)
      const vy = (this.keys.down ? 1 : 0) - (this.keys.up ? 1 : 0)
      const scale = this.keys.slow ? 0.5 : 1
      this.kx = vx * PAD_RADIUS * scale
      this.ky = vy * PAD_RADIUS * scale
    },
    async handleStop() {
      if (!this.active) return
      this.tapStart = 0
      try { await vacuumApi.rcStop() } catch (e) { /* still */ }
      this.lastSent = { omega: 0, velocity: 0 }
    },
    // Skaliert omega in den ersten TAP_RAMP_MS nach Tastendruck linear von 0.5
    // auf 1.0 — kurzes Antippen dreht so nur halb so weit wie ein Halten.
    omegaScale() {
      if (!this.tapStart || !this.anyDirectionKey()) return 1
      const age = Date.now() - this.tapStart
      if (age >= TAP_RAMP_MS) return 1
      return 0.5 + (age / TAP_RAMP_MS) * 0.5
    },
    currentSendValues() {
      return {
        omega: this.omega * this.omegaScale(),
        velocity: this.velocity,
      }
    },
    async sendImmediate() {
      if (!this.active) return
      // Drosseln: Pointermove kann 60×/s feuern — wir halten uns ans Loop-Intervall.
      const now = Date.now()
      if (now - this.lastSendAt < SEND_INTERVAL_MS) return
      this.lastSendAt = now
      const { omega, velocity } = this.currentSendValues()
      try {
        await vacuumApi.rcMove(omega, velocity, SEND_DURATION_MS)
        this.lastSent = { omega, velocity }
      } catch (e) {
        this.error = 'RC-Move: ' + e.message
      }
    },
    async tickSend() {
      if (!this.active) return
      const { omega, velocity } = this.currentSendValues()
      // Wenn Knob in der Mitte und schon Nullen gesendet wurden, gar nicht senden.
      if (Math.abs(omega) < 0.05 && Math.abs(velocity) < 0.02) {
        if (this.lastSent.omega === 0 && this.lastSent.velocity === 0) return
        if (this.lastSent.omega === null) return
      }
      // Bei gehaltener Richtung (Werte unverändert ggü. letztem Send) nur jeden HOLD_SKIP_FACTOR-ten Tick feuern.
      const unchanged = this.lastSent.omega !== null
        && Math.abs(omega - this.lastSent.omega) < 0.01
        && Math.abs(velocity - this.lastSent.velocity) < 0.01
      if (unchanged) {
        this.holdSkipCount = (this.holdSkipCount + 1) % HOLD_SKIP_FACTOR
        if (this.holdSkipCount !== 0) return
      } else {
        this.holdSkipCount = 0
      }
      this.lastSendAt = Date.now()
      try {
        await vacuumApi.rcMove(omega, velocity, SEND_DURATION_MS)
        this.lastSent = { omega, velocity }
      } catch (e) {
        this.error = 'RC-Move: ' + e.message
      }
    },
    async applyFan(level) {
      if (this.busyFan || level === this.currentFan) return
      this.busyFan = true
      this.overrideFan = level
      try { await vacuumApi.setFan(level) }
      catch (e) { this.overrideFan = null; this.error = 'Saugkraft: ' + e.message }
      finally { this.busyFan = false }
    },
    async applyWater(level) {
      if (this.busyWater || level === this.currentWater) return
      this.busyWater = true
      this.overrideWater = level
      try { await vacuumApi.setWater(level) }
      catch (e) { this.overrideWater = null; this.error = 'Wasser: ' + e.message }
      finally { this.busyWater = false }
    },
    async applyMop(mode) {
      if (this.busyMop || mode === this.currentMop) return
      this.busyMop = true
      this.overrideMop = mode
      try { await vacuumApi.setMop(mode) }
      catch (e) { this.overrideMop = null; this.error = 'Wischen: ' + e.message }
      finally { this.busyMop = false }
    },
  },
}
</script>

<style scoped>
.joystick-pad {
  width: 240px;
  height: 240px;
  margin: 0 auto;
  border-radius: 50%;
  background: radial-gradient(circle at center, #2a2a2a 0%, #1a1a1a 70%, #111 100%);
  position: relative;
  touch-action: none;
  user-select: none;
  border: 1px solid #444;
  box-shadow: inset 0 0 16px rgba(0,0,0,0.6);
}
.pad-axis {
  position: absolute;
  background: rgba(255,255,255,0.06);
}
.pad-axis-x {
  left: 8%;
  right: 8%;
  top: 50%;
  height: 1px;
  transform: translateY(-50%);
}
.pad-axis-y {
  top: 8%;
  bottom: 8%;
  left: 50%;
  width: 1px;
  transform: translateX(-50%);
}
.pad-knob {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 60px;
  height: 60px;
  margin-left: -30px;
  margin-top: -30px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #ff8a80, #d32f2f);
  box-shadow: 0 4px 10px rgba(0,0,0,0.55);
  transition: transform 80ms ease-out;
  pointer-events: none;
}
.readout {
  font-family: monospace;
  font-size: 12px;
  color: rgba(255,255,255,0.75);
}
.readout-label {
  color: rgba(255,255,255,0.45);
  margin-right: 4px;
}
.readout-sep {
  margin: 0 10px;
  color: rgba(255,255,255,0.25);
}
.hint {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
}
.hint .v-icon {
  vertical-align: -2px;
  margin-right: 4px;
  color: rgba(255,255,255,0.45);
}
.opt-group {
  text-align: center;
}
.opt-label {
  font-size: 11px;
  color: rgba(255,255,255,0.55);
  margin-bottom: 4px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.opt-group ::v-deep .v-btn:not(.v-btn--active):not(.v-btn--disabled):hover {
  background-color: rgba(255, 138, 128, 0.18) !important;
  color: #ff8a80;
}
.opt-group ::v-deep .v-btn:not(.v-btn--active):not(.v-btn--disabled):hover .v-icon {
  color: #ff8a80;
}
.opt-group ::v-deep .v-btn--active:hover {
  filter: brightness(1.15);
}
</style>
